import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Brain, 
  BarChart3, 
  Settings, 
  RefreshCw, 
  Clock, 
  Target, 
  TrendingUp,
  History,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import type { BotStatistics } from '@/services/BotStatisticsService'
import type { PatternUpdate } from '@/services/pattern-update-service'
import { BotPredictionSettings } from './BotPredictionSettings'
import { BotPatternProcessingSettings } from './BotPatternProcessingSettings'
import { SignalFlowDiagram } from './SignalFlowDiagram'

interface BotWithStats {
  id: string
  name: string
  win_threshold?: number
  max_sequence_length?: number
  pattern_processing_enabled?: boolean
  linked_trading_file_id?: string
  min_pattern_length?: number
  max_pattern_length?: number
  min_pattern_frequency?: number
  statistics: BotStatistics
  pattern_updates?: PatternUpdate[]
}

interface BotDetailDialogProps {
  bot: BotWithStats | null
  isOpen: boolean
  onClose: () => void
  onSettingsUpdate: (
    botId: string,
    winThreshold: number,
    maxSequenceLength: number,
    minPatternLength: number,
    maxPatternLength: number,
    minPatternFrequency: number
  ) => void
  onPatternSettingsUpdate: (botId: string, enabled: boolean) => void
  onRePrediction: (bot: BotWithStats) => void
  repredictingBots: Set<string>
}

export const BotDetailDialog = ({
  bot,
  isOpen,
  onClose,
  onSettingsUpdate,
  onPatternSettingsUpdate,
  onRePrediction,
  repredictingBots
}: BotDetailDialogProps) => {
  const [showPatternUpdates, setShowPatternUpdates] = useState(false)
  
  if (!bot) return null

  const stats = bot.statistics
  const wrongPredictions = stats.total_predictions - stats.skip_predictions - stats.correct_predictions

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            {bot.name} - Trading Bot Details
          </DialogTitle>
          <DialogDescription>
            Comprehensive view of bot performance, settings, and pattern analysis
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="patterns">Patterns</TabsTrigger>
            <TabsTrigger value="analysis">Analysis</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Quick Actions */}
            <div className="flex items-center justify-end gap-2 p-3 bg-muted/30 rounded-lg">
              <button
                onClick={() => onRePrediction(bot)}
                disabled={repredictingBots.has(bot.id)}
                className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${repredictingBots.has(bot.id) ? 'animate-spin' : ''}`} />
                Re-predict
              </button>
            </div>

            {/* Current Prediction Status */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Current Prediction Status
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center space-y-2">
                  <p className="text-xs text-muted-foreground">Prediction</p>
                  <Badge 
                    variant={stats.current_prediction === 'WIN' ? 'default' : 'destructive'}
                    className="text-sm px-3 py-1"
                  >
                    {stats.current_prediction}
                  </Badge>
                </div>
                
                <div className="text-center space-y-2">
                  <p className="text-xs text-muted-foreground">Accuracy</p>
                  <p className={`text-lg font-bold ${
                    stats.accuracy_rate >= 70 ? 'text-success' :
                    stats.accuracy_rate >= 60 ? 'text-warning' : 'text-destructive'
                  }`}>
                    {stats.accuracy_rate.toFixed(1)}%
                  </p>
                </div>
                
                <div className="text-center space-y-2">
                  <p className="text-xs text-muted-foreground">Correct</p>
                  <p className="text-lg font-bold text-success">{stats.correct_predictions}</p>
                </div>
                
                <div className="text-center space-y-2">
                  <p className="text-xs text-muted-foreground">Wrong</p>
                  <p className="text-lg font-bold text-destructive">{wrongPredictions}</p>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Performance Metrics
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Win Rate</span>
                    <span className="font-medium">{stats.accuracy_rate.toFixed(1)}%</span>
                  </div>
                  <Progress 
                    value={stats.accuracy_rate} 
                    className="h-2"
                    indicatorClassName={
                      stats.accuracy_rate >= 70 ? 'bg-success' :
                      stats.accuracy_rate >= 60 ? 'bg-warning' : 'bg-destructive'
                    }
                  />
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Total Predictions</p>
                  <p className="text-lg font-bold">{stats.total_predictions}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Skip Predictions</p>
                  <p className="text-lg font-bold text-muted-foreground">{stats.skip_predictions}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Loss Probability</p>
                  <p className="text-lg font-bold text-destructive">
                    {(stats.loss_probability || 0).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Last Update Info */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timing Information
              </h4>
              <p className="text-sm text-muted-foreground">
                Last prediction: {stats.last_prediction_at 
                  ? new Date(stats.last_prediction_at).toLocaleString()
                  : 'Never'
                }
              </p>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <div className="space-y-6">
              <BotPredictionSettings
                botId={bot.id}
                currentWinThreshold={bot.win_threshold || 0.7}
                currentMaxSequenceLength={bot.max_sequence_length || 12}
                currentMinPatternLength={bot.min_pattern_length || 3}
                currentMaxPatternLength={bot.max_pattern_length || 8}
                currentMinPatternFrequency={bot.min_pattern_frequency || 2}
                onSettingsUpdate={(winThreshold, maxSequenceLength, minPatternLength, maxPatternLength, minPatternFrequency) => {
                  onSettingsUpdate(bot.id, winThreshold, maxSequenceLength, minPatternLength, maxPatternLength, minPatternFrequency)
                }}
              />
              
              <BotPatternProcessingSettings
                botId={bot.id}
                botName={bot.name}
                patternProcessingEnabled={bot.pattern_processing_enabled || false}
                onSettingsUpdate={(enabled) => onPatternSettingsUpdate(bot.id, enabled)}
              />
            </div>
          </TabsContent>

          <TabsContent value="patterns" className="space-y-4">
            {/* Current Pattern Analysis */}
            {stats.analyzed_sequence && (
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Current Analysis Pattern
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Pattern:</span>
                    <div className="font-mono text-sm bg-background/50 px-2 py-1 rounded border">
                      {stats.analyzed_sequence.split('').map((value, index) => (
                        <span 
                          key={index}
                          className={`mx-0.5 ${
                            value === '1' ? 'text-success font-bold' : 
                            value === '0' ? 'text-destructive font-bold' : 
                            'text-muted-foreground'
                          }`}
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    Pattern Length: {stats.analyzed_sequence.length} | 
                    Win Count: {(stats.analyzed_sequence.match(/1/g) || []).length} | 
                    Loss Count: {(stats.analyzed_sequence.match(/0/g) || []).length}
                  </div>
                </div>
              </div>
            )}

            {/* Pattern Updates */}
            {bot.pattern_updates && bot.pattern_updates.length > 0 && (
              <div className="bg-muted/30 rounded-lg p-4">
                <button 
                  onClick={() => setShowPatternUpdates(!showPatternUpdates)}
                  className="flex items-center gap-2 text-sm font-medium mb-3 hover:text-primary transition-colors"
                >
                  {showPatternUpdates ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <History className="h-4 w-4" />
                  Recent Pattern Updates ({bot.pattern_updates.length})
                </button>
                
                {showPatternUpdates && (
                  <div className="space-y-2 mt-3">
                    {bot.pattern_updates.map((update, index) => (
                      <div key={index} className="text-xs bg-background/30 p-2 rounded border">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium font-mono">{update.pattern}</span>
                          <span className="text-muted-foreground">
                            {new Date(update.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Frequency:</span> {update.old_frequency} → {update.new_frequency}
                          </div>
                          <div>
                            <span className="font-medium">Success Rate:</span> {update.old_success_rate.toFixed(1)}% → {update.new_success_rate.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analysis" className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Signal Flow Analysis
              </h4>
              <SignalFlowDiagram
                botName={bot.name}
                patternProcessingEnabled={bot.pattern_processing_enabled || false}
              />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}